import { useEffect, useState } from 'react';

import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ClockIcon from '@/assets/svgs/clock.svg?react';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

type TimePickerProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  disabledDates?: (date: Date) => boolean;
  dateFormat?: string;
  hideSubText?: boolean;
  /** Maximum allowed time in HH:mm format. If user selects a time after this, it resets to previous value */
  maxTime?: string;
  disabled?: boolean;
};

const DEFAULT_TIME = '00:00';
const COMPLETE_TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

const isTimeAfter = (time: string, maxTime: string): boolean => {
  const [hours, minutes] = time.split(':').map(Number);
  const [maxHours, maxMinutes] = maxTime.split(':').map(Number);
  return hours > maxHours || (hours === maxHours && minutes > maxMinutes);
};

/**
 * Wraps a native `<input type="time">` but holds the edited value in local state
 * while the user types, only pushing to react-hook-form once the value is a
 * complete, valid `HH:mm`.
 *
 * A fully controlled native time input re-renders on every keystroke, which
 * clears the browser's "waiting for the second hours digit" buffer — so the user
 * could never type a two-digit hour (it appeared capped, e.g. couldn't go past
 * the first digit). Keeping the in-progress string local until it's complete lets
 * the native field finish its own multi-key edit before we sync the form value.
 */
const TimePickerInput = ({
  id,
  value,
  maxTime,
  disabled,
  onChange,
  onBlur,
}: {
  id: string;
  value: string;
  maxTime?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
}) => {
  const fieldValue = value || DEFAULT_TIME;
  const [draft, setDraft] = useState(fieldValue);

  // Sync the local draft when the committed form value changes from the outside
  // (reset, programmatic setValue, etc.) — but not while the user is mid-edit.
  useEffect(() => {
    setDraft(fieldValue);
  }, [fieldValue]);

  const commit = (next: string) => {
    if (maxTime && next && isTimeAfter(next, maxTime)) {
      // Reject a time past the allowed max — snap back to the committed value.
      setDraft(fieldValue);
      return;
    }
    onChange(next);
  };

  return (
    <Input
      id={id}
      value={draft}
      type="time"
      disabled={disabled}
      onChange={e => {
        const next = e.target.value;
        setDraft(next);
        // Only push complete, valid values up so partial edits (e.g. a single
        // hours digit) don't trigger a controlled re-render mid-keystroke.
        if (COMPLETE_TIME_REGEX.test(next)) {
          commit(next);
        }
      }}
      onBlur={() => {
        // On blur, commit whatever is there (empty clears to default) so the
        // form value never lags behind the last valid edit.
        commit(draft || DEFAULT_TIME);
        onBlur();
      }}
      onKeyDown={e => {
        // Prevent native picker on space key
        if (e.key === ' ') e.preventDefault();
      }}
      className="w-full appearance-none border-none bg-background pl-8 pr-12 focus:outline-none focus:ring-0 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
    />
  );
};

export const TimePicker = <T extends FieldValues>({
  name,
  control,
  label = 'Set the time:',
  hideSubText,
  maxTime,
  disabled,
}: TimePickerProps<T>) => {
  const { t } = useTranslation('common');

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <div className="grid gap-2">
              {label && (
                <FormLabel htmlFor={name} className="text-sm font-medium leading-none">
                  {label}
                </FormLabel>
              )}
              <div className="flex h-[40px] w-full items-center overflow-hidden rounded-md border">
                <div className="relative w-full">
                  <span className="pointer-events-none absolute left-3 top-1/2 h-[1em] w-[1em] -translate-y-1/2 text-muted-foreground">
                    <ClockIcon className="h-[1em] w-[1em]" />
                  </span>
                  <TimePickerInput
                    id={name}
                    value={field.value}
                    maxTime={maxTime}
                    disabled={disabled}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                  {!hideSubText && (
                    <span className="text-bg-foreground absolute right-3 top-1/2 -translate-y-1/2 select-none text-sm">
                      {t('form.timePicker.subText')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </FormControl>
        </FormItem>
      )}
    />
  );
};
